import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useAuth0 } from '@auth0/auth0-react';
import setToken from '../utils/api';
import useDebouncedValue from './useDebouncedValue';

// How long typing in a text filter can pause before the list is asked for again.
export const FILTER_SETTLE_MS = 300;

export interface PagedQueryArgs {
  limit: number;
  page: number;
  filter?: Record<string, unknown>;
}

// One page of a paginated list endpoint (orders, Thunderbird orders, users). The previous
// page stays on screen while the next one loads; nothing refetches on window focus. Filter
// changes are debounced so a typed search asks once it settles, not on every keystroke;
// page and page-size changes go out at once.
export default function usePagedQuery<T>(
  key: string,
  url: string,
  { limit, page, filter = {} }: PagedQueryArgs,
) {
  const { getAccessTokenSilently } = useAuth0();
  const filterKey = useDebouncedValue(JSON.stringify(filter), FILTER_SETTLE_MS);
  const settledFilter = useMemo(
    () => JSON.parse(filterKey) as Record<string, unknown>,
    [filterKey],
  );
  return useQuery<T, Error>(
    [key, limit, page, filterKey],
    async () => {
      const token = await getAccessTokenSilently();
      const api = await setToken(token);
      const response = await api.get<T>(url, {
        params: { page, pageSize: limit, ...settledFilter },
      });
      if (response.status === 200) return response.data;
      throw new Error(`Could not load ${url}`);
    },
    { keepPreviousData: true, refetchOnWindowFocus: false },
  );
}
